import { POIUtility } from '../models/POI';
import type { ImportedWaypoint } from './ImportFileHelper';
import { i18n } from '../../i18n';

export type PoiHighlightInput = {
  type: string;
  name?: string | null;
  serpName?: string | null;
  description?: string | null;
  lng: number;
  lat: number;
  /** The place's class — Accommodation or Amenity, any case — when it's a known place. */
  poiClass?: string | null;
  /** The place's id, with poiClass. */
  poiId?: string | number | null;
};

/** The kind of place a highlight can link back to (HikeHighlight::POI_CLASSES). */
export type HighlightPoiClass = 'Accommodation' | 'Amenity';

export type HikeHighlightPayload = {
  type: string;
  title: string;
  description: string;
  coordinates: [number, number];
  images: [];
  poiType?: HighlightPoiClass;
  poiId?: string;
};

/**
 * The link a highlight keeps to the place it was made from, or null when
 * there's no usable one. The class can come in any case ("accommodation" or
 * "Accommodation"), and the id as a number or a string.
 */
export function highlightPoiLink(poiClass: string | null | undefined, poiId: string | number | null | undefined): { poiType: HighlightPoiClass; poiId: string } | null {
  if (!poiClass || poiId == null || poiId === '') return null;
  const normalised = poiClass.charAt(0).toUpperCase() + poiClass.slice(1).toLowerCase();
  if (normalised !== 'Accommodation' && normalised !== 'Amenity') return null;
  return { poiType: normalised, poiId: String(poiId) };
}

export function poiToHikeHighlight(poi: PoiHighlightInput): HikeHighlightPayload {
  return {
    type: POIUtility.fromString(poi.type).serverType(),
    title: poi.name || poi.serpName || poi.type,
    description: poi.description || '',
    coordinates: [poi.lng, poi.lat],
    images: [],
    ...(highlightPoiLink(poi.poiClass, poi.poiId) ?? {}),
  };
}

export type ImportedWaypointHighlight = {
  type: string;
  title: string;
  description: string;
  coordinates: [number, number];
};

/**
 * Map a waypoint pulled from an imported file (GPX <wpt>, KML placemark, GeoJSON
 * Point) into the payload shape the `addHikeHighlight` store mutation expects.
 * Imported waypoints carry no POI category, so they default to the generic
 * "other" type and fall back to a sensible title when unnamed.
 */
export function importedWaypointToHikeHighlight(wp: ImportedWaypoint): ImportedWaypointHighlight {
  return {
    type: wp.type && wp.type.trim() ? wp.type : 'other',
    title: (wp.name && wp.name.trim()) || i18n.global.t('utils.poi_to_hike_highlight_helper.imported_waypoint'),
    description: wp.description || '',
    coordinates: [wp.lng, wp.lat],
  };
}
